// Cooking mode's state: which stage you're on, what's gone in (the side
// panel's Used rows: guessed from the steps behind you, a tap overrides), and
// the step timers. Ported from the approved prototype (feature pack
// reference/src/cooking.ts), with one change: a running timer stores when it
// ends (`endsAt`, wall clock) rather than counting down a number once a second.
// A phone throttles or suspends timers in the background and on screen lock;
// a deadline stays right through both, and `tick(state, now)` only reads it.
//
// Stages: -1 = "Before you start" (an overview, nothing to tick), 0..n-1 = the
// steps, n = "All steps done".

import { stepUses } from './stepUses'

export interface Timer {
  /** Seconds the timer was set for, +1 min included. */
  total: number
  /** Seconds left when paused (and as of the last tick when running). */
  left: number
  /** Wall-clock ms it ends at while running; null when paused or done. */
  endsAt: number | null
  done: boolean
}

export interface CookState {
  step: number
  direction: 'forward' | 'back'
  startedAt: number
  timers: Record<number, Timer>
  /** Ingredients a tap said are in the pot (true) or not yet (false), by index; unset follows the steps (isUsed). */
  used: Record<number, boolean>
  /** A session from before the overview kept what had been got out; nothing reads it now. */
  ready?: Record<number, boolean>
}

type Steps = { steps: { minutes: number | null }[] }

export const startCooking = (now: number): CookState =>
  ({ step: -1, direction: 'forward', startedAt: now, timers: {}, used: {} })

export function goTo(s: CookState, to: number, r: Steps): CookState {
  const t = Math.max(-1, Math.min(r.steps.length, to))
  return t === s.step ? s : { ...s, step: t, direction: t > s.step ? 'forward' : 'back' }
}

/**
 * For each ingredient, the last step whose text names it (lib/stepUses.ts),
 * or null when no step does — such an ingredient never dims by itself.
 */
export function lastUse(steps: { text: string }[], names: string[]): (number | null)[] {
  const last: (number | null)[] = names.map(() => null)
  steps.forEach((st, k) => { for (const i of stepUses(st.text, names)) last[i] = k })
  return last
}

/** In the pot: what a tap said, else every step that names it is behind you. */
export function isUsed(s: CookState, i: number, last: (number | null)[]): boolean {
  const said = s.used?.[i]
  if (said !== undefined) return said
  const l = last[i]
  return l !== null && l !== undefined && l < s.step
}

/** A tap says the opposite of what the row shows, and that sticks. */
export const toggleUsed = (s: CookState, i: number, last: (number | null)[]): CookState =>
  ({ ...s, used: { ...s.used, [i]: !isUsed(s, i, last) } })

/** The steps' own minutes added up: the overview's "about 35 min"; 0 when no step says. */
export const totalMinutes = (r: { steps: { minutes: number | null }[] }) =>
  r.steps.reduce((sum, st) => sum + (st.minutes ?? 0), 0)

/** Whole seconds left, rounded up so a timer shows 0:00 only when it's done. */
export function secondsLeft(t: Timer, now: number): number {
  return t.endsAt === null ? t.left : Math.max(0, Math.ceil((t.endsAt - now) / 1000))
}

/** Start (or restart a finished one), pause, resume. Steps without minutes have no timer. */
export function toggleTimer(s: CookState, i: number, r: Steps, now: number): CookState {
  const minutes = r.steps[i]?.minutes
  if (!minutes) return s
  const cur = s.timers[i]
  let next: Timer
  if (!cur || cur.done) next = { total: minutes * 60, left: minutes * 60, endsAt: now + minutes * 60_000, done: false }
  else if (cur.endsAt !== null) next = { ...cur, left: secondsLeft(cur, now), endsAt: null }
  else next = { ...cur, endsAt: now + cur.left * 1000 }
  return { ...s, timers: { ...s.timers, [i]: next } }
}

export function addMinute(s: CookState, i: number, r: Steps, now: number): CookState {
  const minutes = r.steps[i]?.minutes
  if (!minutes) return s
  const cur = s.timers[i]
  const next: Timer = cur
    ? {
        total: cur.total + 60,
        left: secondsLeft(cur, now) + 60,
        endsAt: cur.endsAt === null ? null : cur.endsAt + 60_000,
        done: false,
      }
    : { total: minutes * 60 + 60, left: minutes * 60 + 60, endsAt: null, done: false }
  return { ...s, timers: { ...s.timers, [i]: next } }
}

export const resetTimer = (s: CookState, i: number): CookState => {
  const timers = { ...s.timers }
  delete timers[i]
  return { ...s, timers }
}

/** Brings every running timer up to `now` — on any step, not just the open one —
 *  and reports which ones finished since the last tick. */
export function tick(s: CookState, now: number): { state: CookState; finished: number[] } {
  const finished: number[] = []
  const timers: Record<number, Timer> = {}
  for (const [k, t] of Object.entries(s.timers)) {
    if (t.endsAt === null) { timers[+k] = t; continue }
    const left = secondsLeft(t, now)
    if (left === 0) { timers[+k] = { ...t, left: 0, endsAt: null, done: true }; finished.push(+k) }
    else timers[+k] = { ...t, left }
  }
  return { state: { ...s, timers }, finished }
}

export const clock = (sec: number) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`

/** `planned`: cooking a meal from the plan, which the last stage marks as cooked; from a recipe there is nothing to mark. */
export const nextLabel = (step: number, n: number, planned = true) =>
  step === -1 ? 'Start cooking' : step === n - 1 ? 'Finish' : step === n ? (planned ? 'Mark as cooked' : 'Done') : 'Next step'

/** "0 min" for the first minute, then "12 min": time in the kitchen, quietly. */
export function elapsedLabel(startedAt: number, now: number): string {
  return `${Math.max(0, Math.floor((now - startedAt) / 60_000))} min`
}

/** The timer card's state word. */
export function timerState(t: Timer | undefined): 'Timer' | 'Running' | 'Paused' | 'Done' {
  if (!t) return 'Timer'
  if (t.done) return 'Done'
  return t.endsAt !== null ? 'Running' : 'Paused'
}
